import type { ReactNode } from "react";
import type { VerifyComplianceDetails } from "@/models/responses/verify-compliance/verify-compliance.response";
import { formatLongDate } from "@/utils/dateFormatter";
import {
  BuildingIcon,
  CalendarIcon,
  CheckCircleIcon,
  FileTextIcon,
  InfoIcon,
  UsersIcon,
  XCircleIcon,
} from "./icons";

const PCC_INFO =
  "A Pension Clearance Certificate (PCC) is an official certificate issued by PenCom confirming that your organisation's pension obligations are up to date.";

type DetailRowProps = {
  icon: ReactNode;
  label: string;
  value: ReactNode;
  mono?: boolean;
};

const DetailRow = ({ icon, label, value, mono = false }: DetailRowProps) => (
  <div className="flex items-center space-x-3">
    <span className="text-gray-400">{icon}</span>
    <div>
      <p className="text-sm text-gray-500">{label}</p>
      <p className={`font-medium ${mono ? "font-mono" : ""}`}>{value}</p>
    </div>
  </div>
);

const ComplianceBadge = ({ compliant }: { compliant: boolean }) => (
  <div
    className={`flex items-center space-x-2 px-4 py-2 rounded-full shrink-0 ${
      compliant ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"
    }`}
  >
    {compliant ? (
      <CheckCircleIcon className="h-5 w-5" />
    ) : (
      <XCircleIcon className="h-5 w-5" />
    )}
    <span className="font-medium">
      {compliant ? "Active PCC" : "Inactive PCC"}
    </span>
  </div>
);

const PccInfoTooltip = () => (
  <span className="relative inline-flex group">
    <button
      type="button"
      aria-label="What is a PCC?"
      className="text-gray-900 hover:text-gray-700 cursor-help"
    >
      <InfoIcon className="h-4 w-4" />
    </button>
    <span
      role="tooltip"
      className="pointer-events-none absolute right-0 top-full z-20 mt-2 w-64 rounded-lg bg-gray-900 px-3 py-2 text-xs font-normal leading-snug text-white opacity-0 shadow-lg transition-opacity duration-150 group-hover:opacity-100"
    >
      {PCC_INFO}
    </span>
  </span>
);

const CompanyComplianceCard = ({
  details,
}: {
  details: VerifyComplianceDetails;
}) => {
  const { certificate } = details;

  return (
    <div className="bg-white border border-gray-200 rounded-xl p-4 sm:p-8 shadow-sm mb-8">
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between mb-6 gap-3 sm:gap-4">
        <div className="flex items-center space-x-3 sm:space-x-4 min-w-0">
          <div className="bg-gray-100 p-2.5 sm:p-3 rounded-lg shrink-0">
            <BuildingIcon className="h-6 w-6 sm:h-8 sm:w-8 text-gray-600" />
          </div>
          <div className="min-w-0">
            <h2 className="text-xl sm:text-2xl font-bold text-gray-900 break-words">
              {details.companyName}
            </h2>
            {details.sector && (
              <p className="text-gray-600 text-sm sm:text-base">{details.sector}</p>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <ComplianceBadge compliant={details.isCompliant} />
          <PccInfoTooltip />
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        {/* Company Information */}
        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            Company Information
          </h3>
          <DetailRow
            icon={<FileTextIcon className="h-5 w-5" />}
            label="RC Number"
            value={details.cacNumber}
          />
          <DetailRow
            icon={<UsersIcon className="h-5 w-5" />}
            label={
              details.numberOfEmployeesAsAt
                ? `Employees (${details.numberOfEmployeesAsAt})`
                : "Employees"
            }
            value={`${details.numberOfEmployees} employees`}
          />
        </div>

        {/* PCC Status */}
        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            PCC Status
          </h3>

          {certificate ? (
            <>
              <DetailRow
                icon={<FileTextIcon className="h-5 w-5" />}
                label="Certificate ID"
                value={certificate.certificateId}
                mono
              />
              <DetailRow
                icon={<CalendarIcon className="h-5 w-5" />}
                label="Valid Until"
                value={formatLongDate(certificate.validUntil)}
              />
            </>
          ) : (
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
              <p className="font-medium text-gray-900 mb-1">No active PCC</p>
              <p className="text-gray-600 text-sm">
                This company does not currently have an active PenCom Pension
                Clearance Certificate.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CompanyComplianceCard;
